<template>

  <div class="container mt-3">

    <div v-if="status === 'view-all'">

      <div class="desktop-view">
        <div class="search-container mb-2">

          <button
          type="button"
            class="btn btn-primary btn-sm ms-2 mb-2 mb-sm-0"
            data-tippy-content="create new enterprise"
            @click="changeStatus('create')"
            title="add new enterprise"
><font-awesome-icon icon="plus" /></button>

          <button
          type="button" 
            class="btn btn-primary btn-sm ms-2" 
            data-tippy-content="refresh" 
            @click="refreshData"
><font-awesome-icon icon="sync-alt"/></button>
          
          <SearchBox 
            class="ms-3"
            tool-tip="search by enterprise name or actility ref numbers"
            @search="newSearchText($event)" 
            :search-term="searchTerm"
/>

          <PageNav
v-show="showPageNav" 
            :num-pages="pageList.length"
            ref="pageNav"
            @page-change="changePage($event)"
/>
        </div>

        <div v-if="showRows">
          <EnterpriseRow 
            v-for="(ent, index) in curPage.entList" 
            
            :key="ent.enterpriseId" 
            :enterprise="ent" 
            :index="index"

            @edit="changeStatus('edit', $event)"
            @delete="changeStatus('delete', $event)"
/>
        </div> 
      </div>

      <div class="mobile-view">
        <div class="search-container-phone mb-2">

          <PageNav
v-show="showPageNav" 
            :num-pages="pageList.length"
            ref="pageNav"
            class="m-1"
            @page-change="changePage($event)"
/>

          <button
          type="button" 
            class="btn btn-primary btn-sm m-1" 
            @click="refreshData"
><font-awesome-icon icon="sync-alt"/> refresh</button>
          
          <SearchBox 
            class="m-1"
            tool-tip="search by enterprise name or actility ref numbers"
            @search="newSearchText($event)" 
            :search-term="searchTerm"
/>
          
        </div>
        <div v-if="showRows">
          <EnterpriseRowMobile
            v-for="(ent, index) in curPage.entList" 
            
            :key="ent.enterpriseId" 
            :enterprise="ent"
            :index="index"

            @edit="changeStatus('edit', $event)"
            @delete="changeStatus('delete', $event)"
/>
        </div> 
      </div>


      <div v-if="showNoMatches" class="empty-search">
        <p><font-awesome-icon icon="times"/> No enterprises matches your search: '{{searchTerm}}'</p>
      </div>

    </div>
    
    <div v-if="status === 'create'">
      <CreateEnterprise @cancel="changeStatus('view-all')" :enrol-profile-id-list="enrolProfileIdList"/>
    </div>

    <div v-if="status === 'edit'">
      <EditEnterprise @cancel="changeStatus('view-all')" :enterprise="selectedEnterprise" :existing-ent-id-list="[]" :enrol-profile-id-list="enrolProfileIdList"/>
    </div>

    <div v-if="status === 'delete'">
      <DeleteEnterprise @cancel="changeStatus('view-all')" :enterprise="selectedEnterprise"/>
    </div>
    

    <Loading v-if="httpBusy"/>

    <Error :label="error"/>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { ApiService, curApiService, EntPage } from '@/services/ApiService';
import { curPref } from '@/services/Preferences';
import Error from '@/components/Error.vue';
import Loading from '@/components/Loading.vue';
import SearchBox from '@/components/SearchBox.vue';
import PageNav from '@/components/PageNav.vue';
import EnterpriseRow from '@/components/admin/EnterpriseRow.vue';
import EnterpriseRowMobile from '@/components/admin/EnterpriseRowMobile.vue';
import CreateEnterprise from '@/components/admin/CreateEnterprise.vue';
import DeleteEnterprise from '@/components/admin/DeleteEnterprise.vue';
import EditEnterprise from '@/components/admin/EditEnterprise.vue';
import { EnrolProfile, Enterprise } from '@/types';

type Status = 'view-all' | 'create' | 'edit' | 'delete';

const route = useRoute();
const router = useRouter();

const status = ref<Status>('view-all');
const selectedEnterprise = ref<Enterprise>({} as Enterprise);
const httpBusy = ref(false);
const error = ref('');
const pageList = ref<EntPage[]>([]);
const curPage = ref<EntPage>({} as EntPage);
const searchTerm = ref(curPref.retrieve('noc-enterprise-list-search-term', ''));
const enrolProfileList = ref<EnrolProfile[]>([]);
const pageNav = ref<InstanceType<typeof PageNav>>();

// services
const apiService: ApiService = curApiService;

onMounted(() => {
  if (route.params.searchTerm) {
    searchTerm.value = route.params.searchTerm as string;
  }
  refreshData();
});

async function refreshData() {
  httpBusy.value = true;

  const entReq = apiService.getEnterprisesPaginated({ searchTerm: searchTerm.value });
  const enrolReq = apiService.getEnrolProfiles({});

  const [entReply, enrolReply] = await Promise.all([entReq, enrolReq]);

  httpBusy.value = false;

  if (entReply.httpCode === 403 || enrolReply.httpCode === 403) {
    return router.push({ name: 'login' });
  }

  if (entReply.errorMsg) {
    return error.value = `Unable to load enterprises because ${entReply.errorMsg}`;
  }

  if (enrolReply.errorMsg) {
    return error.value = `Unable to load enrolment profiles because ${enrolReply.errorMsg}`;
  }

  pageList.value = entReply.pageList!;
  enrolProfileList.value = enrolReply.enrolProfileList!;

  // we set the current page
  if (pageList.value.length > 0) {
    // we call the reset on the component
    pageNav.value?.reset();
    curPage.value = pageList.value[0];
  }
}

function newSearchText(newSearchText: string) {
  curPref.store('noc-enterprise-list-search-term', newSearchText);
  searchTerm.value = newSearchText;
  refreshData();
}

function changeStatus(newStatus: Status, selectedEnt?: Enterprise) {
  status.value = newStatus;

  if (newStatus === 'view-all') {
    refreshData();
  }

  if (selectedEnt) {
    selectedEnterprise.value = selectedEnt;
  }
}

const showPageNav = computed((): boolean => {
  return pageList.value.length > 0 && !httpBusy.value;
});

const showRows = computed((): boolean => {
  return pageList.value.length > 0 && !httpBusy.value;
});

const showNoMatches = computed((): boolean => {
  return !httpBusy.value && pageList.value.length === 0 && searchTerm.value !== '' && error.value === '';
});

const enrolProfileIdList = computed((): string[] => {
  return enrolProfileList.value.map(enrolProfile => enrolProfile.enrolId);
});

function changePage(newPageNum: number) {
  curPage.value = pageList.value[newPageNum];
}
</script>


<style scoped lang="scss">

.search-container {
  display: flex;
  flex-direction: row-reverse;
  align-items: center;
}

.search-container-phone {
  width: 90%;
  margin-right: auto;
  margin-left: auto;
  display: flex;
  flex-direction: column-reverse;
  align-items: stretch;

}

/* Responsive visibility */
.desktop-view {
  display: block;
}

.mobile-view {
  display: none;
}

@media (max-width: 767px) {
  .desktop-view {
    display: none;
  }
  
  .mobile-view {
    display: block;
  }
}

</style>